"use client";

import { SocialIcon } from "@webdo/community-ui";
import { useEffect, useId, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";
import { communityEmojis, communityErrorKey, communityJson, communityRequest, optimisticReactionToggle, type CommunityTarget, type ReactionSummary, type Reactions } from "../lib/community";
import styles from "./Community.module.css";

type CommunityReactionsProps = {
  targetType: CommunityTarget; targetUuid: string; authenticated: boolean; loginHref: string; initialItems?: ReactionSummary[]; compact?: boolean;
};

export function CommunityReactions(props: CommunityReactionsProps) {
  return <ReactionControl key={`${props.targetType}/${props.targetUuid}/${props.authenticated}`} {...props} />;
}

function ReactionControl({ targetType, targetUuid, authenticated, loginHref, initialItems, compact = false }: CommunityReactionsProps) {
  const t = usePortalTranslations();
  const [state, setState] = useState<Reactions | null>(initialItems ? { items: initialItems, canReact: authenticated } : null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof communityErrorKey> | null>(null);
  const [revision, setRevision] = useState(0);
  const element = useRef<HTMLDivElement>(null);
  const pickerId = useId();
  const pickerToggle = useRef<HTMLButtonElement>(null);
  const mutation = useRef<AbortController | null>(null);
  const requestVersion = useRef(0);
  const path = `${targetType}/${targetUuid}/reactions`;
  const selected = state?.items.find((item) => item.selected);
  const total = state?.items.reduce((sum, item) => sum + item.count, 0);

  useEffect(() => () => { mutation.current?.abort(); }, []);

  useEffect(() => {
    if (initialItems || !element.current) return;
    const controller = new AbortController();
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const version = requestVersion.current;
      communityRequest<Reactions>(path, { signal: controller.signal }).then((result) => { if (!controller.signal.aborted && version === requestVersion.current) setState(result); }).catch((failure: unknown) => {
        if (!controller.signal.aborted && version === requestVersion.current) setError(communityErrorKey(failure));
      });
    }, { rootMargin: "100px" });
    observer.observe(element.current);
    return () => { observer.disconnect(); controller.abort(); };
  }, [initialItems, path, revision]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!element.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { setOpen(false); pickerToggle.current?.focus(); } };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, [open]);

  async function react(emoji: string) {
    if (mutation.current) return;
    if (!authenticated) { setOpen(true); return; }
    if (!state?.canReact) return;
    const restoreFocus = open;
    const previous = state;
    const optimistic = optimisticReactionToggle(previous, emoji);
    const controller = new AbortController();
    mutation.current = controller;
    requestVersion.current += 1;
    setState(optimistic.next);
    setBusy(true); setError(null); setOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => { if (!controller.signal.aborted) pickerToggle.current?.focus(); });
    try {
      const result = await communityRequest<Reactions>(path, { ...communityJson("PUT", { emoji: optimistic.emoji }), signal: controller.signal });
      if (!controller.signal.aborted && mutation.current === controller) setState(result);
    } catch (failure) {
      if (!controller.signal.aborted && mutation.current === controller) { setState(previous); setError(communityErrorKey(failure)); }
    } finally {
      if (!controller.signal.aborted && mutation.current === controller) { mutation.current = null; setBusy(false); }
    }
  }

  return <div ref={element} className={`${styles.reactionControl} ${compact ? styles.compactReactions : ""}`}>
    <div className={styles.reactions} role="group" aria-label={t("community.reaction")}>
      <button type="button" className={`${styles.iconAction} ${selected?.emoji === "❤️" ? styles.liked : ""}`} aria-pressed={selected?.emoji === "❤️"} aria-label={t(selected?.emoji === "❤️" ? "community.unlike" : "community.like")} disabled={busy || (authenticated && !state?.canReact)} onClick={() => void react("❤️")}>
        <SocialIcon name="heart" fill={selected?.emoji === "❤️" ? "currentColor" : "none"} /><span className={styles.reactionCount} aria-label={total === undefined ? t("community.loading") : t("community.totalReactions", { count: total })}>{total ?? "—"}</span>
      </button>
      <button ref={pickerToggle} type="button" className={styles.pickerToggle} aria-label={t("community.addReaction")} aria-expanded={open} aria-controls={pickerId} aria-disabled={busy} onClick={() => { if (!busy) setOpen(!open); }}>{selected && selected.emoji !== "❤️" ? <span aria-hidden="true">{selected.emoji}</span> : <SocialIcon name="smile" />}</button>
    </div>
    {open ? <div className={styles.reactionPicker} id={pickerId}>
      {authenticated && state?.canReact ? <><span className={styles.srOnly}>{t("community.reactionHint")}</span><div className={styles.reactions}>{communityEmojis.map((emoji) => <button key={emoji} type="button" className={styles.reaction} aria-label={t(`community.emoji.${emoji}`)} aria-pressed={selected?.emoji === emoji} disabled={busy} onClick={() => void react(emoji)}>{emoji}</button>)}</div></> : !authenticated ? <a className={styles.loginHint} href={loginHref}>{t("community.loginReact")}</a> : <p className={styles.muted}>{state ? t("community.forbidden") : t("community.loading")}</p>}
    </div> : null}
    {error ? <div role="alert" className={styles.error}>{t(error)} {!state ? <button type="button" className={styles.linkButton} onClick={() => { setError(null); setRevision((value) => value + 1); }}>{t("community.retry")}</button> : null}</div> : null}
  </div>;
}
